import { useEffect, useRef, useState } from "react";

interface ProfileMenuProps {
  /** The email on file for this visitor's token, from /api/link/validate.
   *  Empty until one is known -- the menu says "Anonymous" then. */
  email: string;
  /** The picture a Google login left on the account, or empty for the
   *  blank silhouette. */
  avatarUrl: string;
  /** To the login page, to recover an account saved elsewhere. */
  onLogIn: () => void;
  /** Forget this browser's token. */
  onLogOut: () => void;
  /** Open the "save your account" modal. */
  onAddEmail: () => void;
}

/**
 * The header's profile button and its popdown: who this browser's token
 * belongs to, as far as we know, with the picture from a Google login if
 * there was one.
 */
export function ProfileMenu(props: ProfileMenuProps) {
  const { email, avatarUrl } = props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // A click anywhere outside, or Escape, closes it, as it would any other
  // disclosure that covers part of the page.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div className="profile-menu" ref={rootRef}>
      <button
        type="button"
        className="profile-avatar"
        aria-expanded={open}
        aria-controls="profile-popdown"
        aria-label="Profile"
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar url={avatarUrl} />
      </button>
      {open && (
        <div id="profile-popdown" className="profile-popdown" role="dialog" aria-label="Account">
          <AccountSummary
            {...props}
            onLogIn={() => { setOpen(false); props.onLogIn(); }}
            onLogOut={() => { setOpen(false); props.onLogOut(); }}
            onAddEmail={() => { setOpen(false); props.onAddEmail(); }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The profile picture, or a blank silhouette when there isn't one. A picture
 * that fails to load (Google's URLs do expire) falls back to the silhouette
 * rather than a broken-image icon.
 */
function Avatar({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        width="100%"
        height="100%"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  // Drawn, like the nav toggle's icon, so it sits centred in the circle on
  // every platform. A head and shoulders clipped by the circle's own
  // overflow.
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <circle cx="12" cy="9.5" r="4" fill="currentColor" />
      <ellipse cx="12" cy="21" rx="7.5" ry="5.5" fill="currentColor" />
    </svg>
  );
}

/**
 * What the profile knows about this visitor, under a heading that says what
 * it is, and what they can do about it: the desktop popdown and the phone
 * menu (App.tsx) both show it.
 *
 * Only an account with an email offers "Log out". An anonymous one exists
 * only in this browser, so logging out of it would lose it for good; it is
 * offered the email prompt that would make it recoverable instead, and a
 * way to log in to an account saved elsewhere.
 */
export function AccountSummary({ email, avatarUrl, onLogIn, onLogOut, onAddEmail }: ProfileMenuProps) {
  return (
    <div className="account-summary">
      <span className="account-summary-heading">Account</span>
      <span className="account-summary-identity">
        <span className="account-summary-avatar">
          <Avatar url={avatarUrl} />
        </span>
        <span className="account-summary-email">{email || "Anonymous"}</span>
      </span>
      {email ? (
        <button type="button" className="account-summary-action" onClick={onLogOut}>
          Log out
        </button>
      ) : (
        <>
          <button type="button" className="account-summary-prompt" onClick={onAddEmail}>
            Add your email to save this account
          </button>
          <button type="button" className="account-summary-action" onClick={onLogIn}>
            Log in
          </button>
        </>
      )}
    </div>
  );
}
